import { MessageSquare, Phone, Mail, ArrowDown } from 'lucide-react';
import { MarketingHeroPill } from '@/components/marketing/v2/MarketingHeroPill';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';

const WHATSAPP_URL = 'https://wa.me/237656488879';
const PHONE_HREF = 'tel:+237656488879';
const EMAIL_HREF = 'mailto:contact@velko-pos.com';

type Commitment = { channel: string; target: string };

const COMMITMENTS: ReadonlyArray<Commitment> = [
  { channel: 'WhatsApp', target: '≤ 30 min' },
  { channel: 'Téléphone', target: 'Lun-Ven · 8h-18h' },
  { channel: 'Email · Formulaire', target: '≤ 24 h ouvrables' },
  { channel: 'Incident bloquant', target: 'Priorisé sur plan payant' },
];

function scrollToForm() {
  const el = document.getElementById('contact-form-section');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function ContactHeroV2() {
  return (
    <section className="bg-paper pb-16 pt-16 md:pb-20 md:pt-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid items-start gap-12 md:grid-cols-[1.2fr_1fr] md:gap-14">
          {/* LEFT — Headline + quick channels */}
          <div>
            <MarketingHeroPill>Support Velko · Yaoundé · 8h-18h</MarketingHeroPill>

            <h1 className="mt-7 font-marketing-display text-[clamp(48px,7vw,104px)] font-bold leading-[0.9] tracking-[-0.045em] text-charcoal-800">
              Parle-nous.
              <br />
              <span className="underline-lime">On répond.</span>
            </h1>

            <p className="mt-7 max-w-xl font-marketing-body text-lg leading-[1.55] text-charcoal-600 md:text-xl">
              Une équipe basée à Yaoundé, pas un chatbot. On t'écoute en français, sur ton canal,
              dans tes horaires. Pour les questions avant achat comme pour les urgences de caisse.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-lime-500 px-4 py-2.5 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-4 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6"
              >
                <MessageSquare className="h-4 w-4" strokeWidth={2.5} />
                WhatsApp · +237 656 48 88 79
              </a>
              <a
                href={PHONE_HREF}
                className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-white px-4 py-2.5 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-4 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6"
              >
                <Phone className="h-4 w-4 text-blue-500" strokeWidth={2.5} />
                Appeler
              </a>
              <a
                href={EMAIL_HREF}
                className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-white px-4 py-2.5 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-4 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6"
              >
                <Mail className="h-4 w-4 text-terracotta-500" strokeWidth={2.5} />
                Email
              </a>
            </div>

            <div className="mt-7">
              <NeobrutalButton variant="dark" size="md" onClick={scrollToForm}>
                Ou remplir le formulaire
                <ArrowDown className="h-4 w-4" strokeWidth={2.5} />
              </NeobrutalButton>
            </div>
          </div>

          {/* RIGHT — Dark SLA commitment panel */}
          <aside className="border-[3px] border-charcoal-800 bg-charcoal-800 p-8 shadow-neobrutal-lime-10 md:p-9">
            <div className="flex items-center justify-between border-b border-charcoal-700 pb-4">
              <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
                Notre engagement
              </span>
              <span className="inline-flex items-center gap-1.5 border-2 border-charcoal-800 bg-lime-500 px-2.5 py-1 font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-charcoal-800">
                <span className="h-1.5 w-1.5 rounded-full bg-charcoal-800 animate-[pulse_2s_ease-in-out_infinite]" />
                Support actif
              </span>
            </div>

            <div className="mt-6">
              <div className="font-marketing-display text-[64px] font-bold leading-none tracking-[-0.04em] text-lime-500 md:text-[72px]">
                &lt; 24h
              </div>
              <p className="mt-3 font-marketing-body text-sm leading-[1.55] text-charcoal-200">
                Engagement sur toutes les demandes reçues par formulaire, email ou WhatsApp, jours
                ouvrables. Si on dépasse, on te le dit — sans excuse automatique.
              </p>
            </div>

            <ul className="mt-6 space-y-2.5">
              {COMMITMENTS.map(({ channel, target }) => (
                <li
                  key={channel}
                  className="flex items-center justify-between border-l-[3px] border-lime-500 bg-charcoal-700 px-4 py-3"
                >
                  <span className="font-marketing-body text-[13px] text-charcoal-200">
                    {channel}
                  </span>
                  <span className="font-marketing-mono text-[12px] font-semibold text-lime-400">
                    {target}
                  </span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
}
